/**
 * 會員相片上載（前端共用；`decisions/012`）
 *
 * 流程：揀圖 → **縮圖**（canvas，最長邊 512px JPEG）→ 攞簽名 → 直上 Cloudinary → 回 `secure_url`。
 * ⚠️ 用共用模組 `@coeldery/cloudinary-upload`（唔自己寫 fetch ；同 85AI／family-tree 同一契約）。
 * ⚠️ 失敗**一定照實報**（分類原因交呼叫者本地化）；唔准扮成功。
 */
import { uploadToCloudinary } from '@coeldery/cloudinary-upload';

/** 上載前縮到最長邊 512px（卡上頭像只 62px，512 已經有餘） */
export const PHOTO_MAX_EDGE = 512;

/** 原檔上限（避免載入超大相爆記憶體；Cloudinary 側亦有 5MB 限制） */
export const PHOTO_MAX_INPUT_BYTES = 12 * 1024 * 1024;

export type PhotoFailReason = 'too_big' | 'config' | 'sign' | 'upload' | 'read' | 'network';

export type PhotoUploadResult = { ok: true; url: string } | { ok: false; reason: PhotoFailReason; message?: string };

/**
 * 圖檔 → JPEG Blob（最長邊 `maxEdge`）。
 * ⚠️ 喺瀏覽器做（canvas）→ 唔需要 server 處理、唔加依賴、上載細好多。
 */
export async function imageFileToJpeg(file: File, maxEdge = PHOTO_MAX_EDGE): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');
  ctx.drawImage(bitmap, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.85));
  if (!blob) throw new Error('toBlob failed');
  return blob;
}

/** 揀咗嘅圖 → 縮圖 → 上 Cloudinary；回 `{ ok:true, url }` 或 `{ ok:false, reason }` */
export async function uploadMemberPhoto(file: File): Promise<PhotoUploadResult> {
  if (file.size > PHOTO_MAX_INPUT_BYTES) return { ok: false, reason: 'too_big' };
  let blob: Blob;
  try {
    blob = await imageFileToJpeg(file);
  } catch {
    return { ok: false, reason: 'read' };
  }
  try {
    const r = await uploadToCloudinary({ file: blob, signUrl: '/api/cloudinary-sign' });
    return r.ok ? { ok: true, url: r.url } : { ok: false, reason: r.reason, message: r.message };
  } catch {
    /* 網絡錯誤（fetch throw）→ 明確分類，唔當係簽名失敗 */
    return { ok: false, reason: 'network' };
  }
}

/** 失敗原因 → i18n key（呼叫者 `t(reasonKeyOf(r.reason))`） */
export const reasonKeyOf = (reason: PhotoFailReason): string =>
  reason === 'too_big'
    ? 'photo_err_big'
    : reason === 'config'
      ? 'photo_err_config'
      : 'photo_err_generic';
